import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import i18n from '../i18n';
import { getMe, setUserPreferences } from '../lib/api';
import { teamsAuth } from '../lib/teams-auth';
import {
  DEFAULT_LANGUAGE,
  type LanguagePreference,
  normalizeToSupportedLanguage,
  type SupportedLanguage,
  TEAMS_LANGUAGE_MODE,
  isSupportedLanguage,
} from '../i18n/languages';
import { useAuth } from './AuthContext';

type LanguageContextValue = {
  language: SupportedLanguage;
  preference: LanguagePreference;
  setPreference: (preference: LanguagePreference) => Promise<void>;
  isLoading: boolean;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

async function resolveTeamsLanguage(): Promise<SupportedLanguage> {
  try {
    const context = await teamsAuth.getContext();
    return normalizeToSupportedLanguage(context?.app?.locale ?? navigator.language);
  } catch {
    return normalizeToSupportedLanguage(navigator.language);
  }
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const [language, setLanguage] = useState<SupportedLanguage>(DEFAULT_LANGUAGE);
  const [preference, setPreferenceState] = useState<LanguagePreference>(TEAMS_LANGUAGE_MODE);
  const [isLoading, setIsLoading] = useState(false);

  const applyLanguage = useCallback(async (nextLanguage: SupportedLanguage) => {
    setLanguage(nextLanguage);
    if (i18n.language !== nextLanguage) {
      await i18n.changeLanguage(nextLanguage);
    }
  }, []);

  const setPreference = useCallback(async (nextPreference: LanguagePreference) => {
    const resolved = nextPreference === TEAMS_LANGUAGE_MODE
      ? await resolveTeamsLanguage()
      : nextPreference;

    setPreferenceState(nextPreference);
    await applyLanguage(resolved);

    if (auth.isAuthenticated) {
      await setUserPreferences({
        language: nextPreference === TEAMS_LANGUAGE_MODE ? null : nextPreference,
      });
    }
  }, [applyLanguage, auth.isAuthenticated]);

  useEffect(() => {
    let cancelled = false;

    const initializeLanguage = async () => {
      setIsLoading(true);
      try {
        if (!auth.isAuthenticated) {
          const unauthenticatedLanguage = auth.isInTeams
            ? await resolveTeamsLanguage()
            : normalizeToSupportedLanguage(navigator.language);
          if (!cancelled) {
            setPreferenceState(TEAMS_LANGUAGE_MODE);
            await applyLanguage(unauthenticatedLanguage);
          }
          return;
        }

        const [me, teamsLanguage] = await Promise.all([
          getMe().catch(() => null),
          resolveTeamsLanguage(),
        ]);

        const storedLanguage = me?.language;
        const nextPreference: LanguagePreference = isSupportedLanguage(storedLanguage)
          ? storedLanguage
          : TEAMS_LANGUAGE_MODE;

        const nextLanguage = nextPreference === TEAMS_LANGUAGE_MODE
          ? teamsLanguage
          : nextPreference;

        if (!cancelled) {
          setPreferenceState(nextPreference);
          await applyLanguage(nextLanguage);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    initializeLanguage().catch((error) => {
      if (!cancelled) {
        console.error('Language initialization failed', error);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [applyLanguage, auth.isAuthenticated, auth.isInTeams]);

  const value = useMemo<LanguageContextValue>(() => ({
    language,
    preference,
    setPreference,
    isLoading,
  }), [language, preference, setPreference, isLoading]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }

  return context;
}
